import { createAsyncThunk } from "@reduxjs/toolkit";
import { ThunkApiConfig } from "@shared/models/redux";
import { fetchUserProfile } from "@user/store/thunks/fetchUserProfile";
import { fetchUserTerms } from "@terms/store/thunks/fetchUserTerms";
import { fetchUserSegmentFunction } from "@user/store/thunks/fetchUserSegmentFunction";
import { fetchUserRolesThunk, fetchUserIFCsThunk } from "@myProfile/store/thunks";

/**
 * Initialize the application by fetching essential user data.
 *
 * Order of operations:
 * 1. Fetch user profile (required for subsequent calls)
 * 2. In parallel: fetch terms, segment functions, user roles, and IFCs
 *
 * User roles and IFCs are fetched at app initialization to determine
 * profile menu visibility (My Profile, Manage Contacts) based on
 * the user's role (Primary Contact, Secondary Contact, etc.) and fund assignments.
 */
export const initializeApp = createAsyncThunk<void, void, ThunkApiConfig>(
  "app/initializeApp",
  async (_, { dispatch }) => {
    try {
      // Step 1: Fetch user profile first (needed for sfdcContactId)
      const userProfile = await dispatch(fetchUserProfile()).unwrap();

      // Step 2: Fetch remaining data in parallel
      const parallelFetches: Promise<unknown>[] = [dispatch(fetchUserTerms()), dispatch(fetchUserSegmentFunction())];

      // Always fetch user roles and IFCs - pass contactId to API and let it respond
      // - Roles: required for determining Manage Contacts visibility (Primary/Secondary)
      // - IFCs: required for determining My Profile visibility (users without IFCs cannot see it)
      // If contactId is null/invalid, API returns "No IFC data found" which thunk handles as empty array
      const contactId = userProfile.sfdcContactId || "";

      parallelFetches.push(dispatch(fetchUserRolesThunk(contactId)));
      parallelFetches.push(dispatch(fetchUserIFCsThunk(contactId)));

      await Promise.all(parallelFetches);
    } catch (error) {
      console.error(error);
    }
  }
);
